import {
  ref,
  Ref,
  nextTick,
  getCurrentInstance,
  ComponentInternalInstance,
  reactive,
  UnwrapRef,
  computed,
} from '@vue/composition-api';
import smh from '@/renderer/scripts/smh';
import storage from '@/renderer/scripts/utils/storage';
import { CONSTANT_URL, httpUrl } from '@/renderer/scripts/http';
import { getWeComAndWeChatLanguage } from '@/renderer/scripts/utils/tools';
import { encodeText } from '@/renderer/scripts/utils/tools/packages/encrypt';
import i18n from '@/locales/i18n';

type Handler = {
  isCustomizedDomain: boolean;
  customId: Ref<string>;
  customIdList: Ref<string[]>;
  getWeComUrlFailed: Ref<boolean>;
  iframeUrl: Ref<string>;
  enableWeCom: Ref<boolean>;
  wecomConfig: UnwrapRef<{
    appid: string;
    agentid: string;
  }>;
  methods: {
    getIframeUrl: (appid: string, agentid: string, redirectUrl: string) => string;
    getRedirectUrl: (realDomain?: string) => string;
    getWeComIframe: () => Promise<void>;
    renderWeComCode: (appid: string, agentid: string, redirectUrL: string) => void;
    getWeComConfig: (domain: string) => Promise<void>;
  };
};
export default function actionHandler(): Handler {
  const vm = (getCurrentInstance() as ComponentInternalInstance).proxy;
  const store = vm.$store;
  // 企业id下拉列表最多存5项
  const customIdListLimit = 5;
  const { currentDomain, isCustomizedDomain } = CONSTANT_URL;
  const iframeUrl = ref('');
  const getWeComUrlFailed = ref(false);
  const customId = ref('');
  const customIdList: Ref<string[]> = ref([]);
  const remoteCropList = computed(() => store.getters.corpList);

  const enableWeCom = ref(false);
  const wecomConfig = reactive({
    appid: '',
    agentid: '',
  });

  const methods = {
    // 自定义域名获取企微二维码链接
    getIframeUrl(appid = '', agentid = '', redirectUrl = '') {
      const lang = getWeComAndWeChatLanguage(i18n.locale);
      return `https://open.work.weixin.qq.com/wwopen/sso/qrConnect?appid=${appid}&agentid=${agentid}&redirect_uri=${redirectUrl}&lang=${lang}`;
    },
    getRedirectUrl(realDomain = '') {
      // 公有云企微扫码只能在pan.tencent.com的回调域名下，前端利用domain参数实现二跳
      // 比如从pan.tencent.com重定向到自定义xxx.pan.tencent.com
      let path = store.getters.isBrowser ? `${window.location.pathname}${window.location.search}` : '';
      if (path) {
        path = encodeURIComponent(encodeURIComponent(encodeText(path)));
      }
      let domain = '';
      let origin = `${store.getters.protocol}://${CONSTANT_URL.defaultDomain}`;
      if (!realDomain || currentDomain !== CONSTANT_URL.defaultDomain) {
        domain = `${store.getters.protocol}://${currentDomain}`;
      }
      // 私有化下不需要二跳（二跳指公有云下第一跳必须是pan.tencent.com域名 二跳到domain对应的域名）
      if (store.getters.isPrivate) {
        domain = '';
        origin = httpUrl.webUrl;
      }
      const redirectUrl = encodeURIComponent(`${origin}/login?domain=${domain}&path=${path}&realdomain=${realDomain}`);
      return redirectUrl;
    },
    // 公共域名获取企微二维码链接
    async getWeComIframe() {
      try {
        getWeComUrlFailed.value = false;
        const domain = `${customId.value}.${currentDomain}`;
        const res = await smh.api.getWeComConfig(domain);
        const { enableWeworkLogin, corpid, agentId } = res.data;
        if (!enableWeworkLogin) {
          getWeComUrlFailed.value = true;
          return;
        }
        const redirectUrl = methods.getRedirectUrl(domain);
        iframeUrl.value = methods.getIframeUrl(corpid, agentId, redirectUrl);
        nextTick(() => {
          methods.renderWeComCode(corpid, agentId, redirectUrl);
        });
        // 成功后本地记录企业id
        methods.addCustomIdList(customId.value);
      } catch (e) {
        getWeComUrlFailed.value = true;
      }
    },
    // 渲染企微二维码
    renderWeComCode(
      appid = '',
      agentid = '',
      redirectUrl = `${store.getters.protocol}://${CONSTANT_URL.defaultDomain}/login`,
    ) {
      // 使用wwlogin_来判断是企微登录的回调
      const state = `wwlogin_${new Date().getTime()}`;
      // @ts-ignore
      const wwLogin = new WwLogin({
        id: 'wx_reg',
        appid,
        agentid,
        redirect_uri: redirectUrl,
        state,
        href: CONSTANT_URL.wecomQrcodeCss, // 覆盖二维码样式需要引入自己的css
        lang: getWeComAndWeChatLanguage(i18n.locale),
      });
    },
    // 企业id操作记录
    async initCustomIdList() {
      if (remoteCropList.value.length > 0) {
        customId.value = remoteCropList.value[0].corpId;
      } else {
        const list = storage.get<string[]>(storage.StoreKey.CUSTOM_ID_LIST) || [];
        // FIX 存量数据存的是对象，改成字符串后兼容处理一下
        customIdList.value = list.filter((item: string) => typeof item === 'string');
        // 默认选中最新一条企业id
        if (list.length > 0) {
          [customId.value] = customIdList.value;
        }
      }
    },
    addCustomIdList(customId: string) {
      if (!customIdList.value.find(item => item === customId)) {
        customIdList.value.unshift(customId);
      }
      if (customIdList.value.length > customIdListLimit) {
        customIdList.value.pop();
      }
      storage.set(storage.StoreKey.CUSTOM_ID_LIST, customIdList.value);
    },

    // 获取企微配置
    async getWeComConfig(domain: string) {
      try {
        const res = await smh.api.getWeComConfig(domain);
        const { enableWeworkLogin, corpid, agentId } = res.data;
        enableWeCom.value = enableWeworkLogin;
        Object.assign(wecomConfig, {
          appid: corpid,
          agentid: agentId,
        });
      } catch (e) {
        enableWeCom.value = false;
      }
    },
  };

  methods.initCustomIdList();

  return {
    isCustomizedDomain,
    iframeUrl,
    getWeComUrlFailed,
    customId,
    customIdList,
    enableWeCom,
    wecomConfig,
    methods,
  };
}
